import React, {useEffect, useState} from 'react'
import {Container, Card} from 'react-bootstrap-v5'
import {FaSortUp, FaSortDown} from 'react-icons/fa'
import {toast} from 'react-toastify'

import {
  getMemberLiveReportListApi,
  getMemberReportListApi,
} from '../../modules/member-report/MemberReportCRUD'
import {getActiveMemberTypeList} from '../../modules/master-page/member-type-master/MemberTypeCRUD'
import {IMemberTypeModel} from '../../models/master-page/IMemberTypeModel'

import {KTSVG} from '../../../_MeldiDham/helpers'
import LoaderInTable from '../common-pages/LoaderInTable'
import BlankDataImageInTable from '../common-pages/BlankDataImageInTable'
import {useDebounce} from '../../../hooks/useDebounce'
import {Pagination} from 'antd'
import {MemberTypeData} from '../other-dropDowns/otherDropDowns'
import {ModelPopUp_ShowImage} from '../../components/modal-popup/ModelPopUp_ShowImage'

/* ===================== TYPES ===================== */
interface IMemberLiveReportModel {
  memberID: number
  memberName: string
  mobileNumber: string
  photoPath: string
  jaapLiveStartTime: string
  isLiveForJaap: boolean
  countryName: string
  countryCode: string
  villageCityName: string
  addressAndText: string
  Pincode: string
}
interface IMemberLive {
  imageShow: any
  loading: boolean
}

/* ===================== STYLES ===================== */
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap');

  
  /* ---- Table ---- */
  .mlr-table-wrap {
    overflow-x: auto;
  }

  .mlr-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
  }

  .mlr-table thead tr {
    background: #f0f3fc;
  }

  .mlr-table thead th {
    padding: 13px 18px;
    color: #5a6490;
    font-weight: 700;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    border-bottom: 2px solid #dde0f0;
    transition: color 0.15s;
  }

  .mlr-table thead th:hover { color: #2e4fa3; }
  .mlr-table thead th.active-sort { color: #2e4fa3; }

  .mlr-sort-icon {
    display: inline-block;
    margin-left: 5px;
    font-size: 11px;
    vertical-align: middle;
  }

  .mlr-table tbody tr {
    border-bottom: 1px solid #f0f2f8;
    transition: background 0.15s;
  }

  .mlr-table tbody tr:last-child { border-bottom: none; }
  .mlr-table tbody tr:hover { background: #f7f9ff; }

  .mlr-table td {
    padding: 13px 18px;
    color: #2c3558;
    vertical-align: middle;
  }

  /* ---- Member Cell ---- */
  .mlr-member-cell {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .mlr-avatar {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    object-fit: cover;
    cursor: pointer;
    border: 2px solid #dde0f0;
    transition: border-color 0.2s, transform 0.15s;
    flex-shrink: 0;
  }

  .mlr-avatar:hover {
    border-color: #2e4fa3;
    transform: scale(1.07);
  }

  .mlr-member-name {
    font-weight: 600;
    color: #1a2040;
    font-size: 14px;
  }

  /* ---- Status Badges ---- */
  .mlr-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 20px;
    padding: 4px 12px;
    font-size: 12.5px;
    font-weight: 700;
    letter-spacing: 0.03em;
  }

  .mlr-status-live {
    background: #dcfce7;
    color: #15803d;
  }

  .mlr-status-offline {
    background: #f1f2f7;
    color: #8891b0;
  }

  .mlr-status-live-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #22c55e;
    animation: pulse-dot 1.4s ease-in-out infinite;
    flex-shrink: 0;
  }

  .mlr-status-offline-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #c0c5d8;
    flex-shrink: 0;
  }

  .mlr-mobile {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #4a5180;
    font-size: 13.5px;
  }

  .mlr-time {
    color: #5a6490;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ---- Pagination ---- */
  .mlr-pagination-bar {
    padding: 16px 24px;
    border-top: 1.5px solid #ebebf0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: #fafbff;
  }

  .mlr-pagination-bar .ant-pagination-item-active {
    background: #2e4fa3 !important;
    border-color: #2e4fa3 !important;
  }

  .mlr-pagination-bar .ant-pagination-item-active a {
    color: #fff !important;
  }
`

/* ===================== COMPONENT ===================== */
const MemberLiveReportList = () => {
  const [data, setData] = useState<IMemberLiveReportModel[]>([])
  const [search, setSearch] = useState('')
  const [memberTypeID, setMemberTypeID] = useState<number>(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [sortColumn, setSortColumn] = useState('jaapLiveStartTime')
  const [sortDirection, setSortDirection] = useState<'DESC' | 'ASC'>('DESC')
  const [loading, setLoading] = useState(false)
  const [state, setState] = useState<IMemberLive>({
    imageShow: process.env.REACT_APP_API_URL,
    loading: false,
  })

  /* ================= Pagination ================= */
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [postPerPage, setPostPerPage] = useState(10)

  /* ================= Search ================= */
  const [name, setName] = useState('')
  const debouncedSearch = useDebounce(name, 2000)
  const pathUrl = process.env.REACT_APP_API_URL
  /* ===================== INIT ===================== */

  useEffect(() => {
    setPage(1)
  }, [debouncedSearch])

  useEffect(() => {
    fetchMembers(debouncedSearch, page, postPerPage, sortColumn, sortDirection)
  }, [debouncedSearch, page, postPerPage, sortColumn, sortDirection])

  const fetchMembers = async (
    debouncedSearch: string,
    page: number,
    postPerPage: number,
    sortColumn: string,
    sortDirection: string
  ) => {
    try {
      setLoading(true)
      const res = await getMemberLiveReportListApi(
        debouncedSearch,
        page,
        postPerPage,
        sortColumn,
        sortDirection
      )
      if (res.data.isSuccess) {
        const resData = res.data.responseObject
        setData(resData)
        if (resData.length > 0) {
          setTotal(resData[0].totalCount) // ✅ IMPORTANT
        } else {
          setTotal(0)
        }
      }
    } catch (error) {
      toast.error('Failed to load member report')
    } finally {
      setLoading(false)
    }
  }

  /* ===================== SORT ===================== */
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'DESC' ? 'ASC' : 'DESC')
    } else {
      setSortColumn(column)
      setSortDirection('DESC')
    }
  }

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) return null
    return sortDirection === 'ASC' ? (
      <span className='bi bi-arrow-up ms-1' title='sort by' />
    ) : (
      <span className='bi bi-arrow-down ms-1' title='sort by' />
    )
  }
  /* ================= Search Input ================= */
  const filter = (e: any) => {
    setName(e.target.value)
  }

  /* ================= Pagination ================= */
  const onShowSizeChange = (current: number, size: number) => {
    setPostPerPage(size)
    setPage(1)
  }

  // ==================== Member Image ============
  const [showImage, setShowImage] = useState(false)
  const handleClose_image = () => {
    setState({...state, imageShow: '', loading: false})
    setShowImage(false)
  }
  const handleShow_Image = (selImg: string) => {
    setState({...state, imageShow: selImg, loading: false})
    setShowImage(true)
  }
  /* ===================== UI ===================== */
  return (
    <>
      {' '}
      <style>{styles}</style>
      <div className='card'>
        {/* ===== FILTER HEADER ===== */}
        <div className='card-header border-0 bg-secondary mb-3 py-4'>
          <div className='d-flex flex-wrap gap-4 align-items-center'>
            {/* Search */}
            <div style={{width: '260px'}}>
              <div className='position-relative'>
                <KTSVG
                  path='/media/icons/duotune/general/gen021.svg'
                  className='svg-icon-2 position-absolute top-50 start-0 translate-middle-y ms-4'
                />
                <input
                  type='text'
                  className='form-control form-control-solid ps-12 bg-white'
                  placeholder='Search member'
                  onChange={(e) => filter(e)}
                  value={name}
                />
              </div>
            </div>

            {/* Member Type */}
            {/* <div style={{width: '260px'}}>
            <select className='form-select bg-white' onChange={selectChange} id='memberTypeID'>
              <option value={0}>Member Type</option>
              {MemberTypeData.map((item) => (
                <option
                  key={item.memberTypeID}
                  value={item.memberTypeID}
                  selected={item.memberTypeID === memberTypeID ? true : false}
                >
                  {item.memberType}
                </option>
              ))}
            </select>
          </div> */}
          </div>
        </div>

        {/* ===== TABLE ===== */}
        <div className='table-responsive'>
          <table className='table table-striped align-middle mb-0'>
            <thead className='bg-primary text-white'>
              <tr className='fw-bolder fs-5 text-white me-2'>
                <th
                  className='ps-2'
                  onClick={() => handleSort('MemberName')}
                  style={{cursor: 'pointer'}}
                >
                  Member Name {renderSortIcon('MemberName')}
                </th>
                <th
                  className=''
                  // onClick={() => handleSort('MobileNumber')}
                  // style={{cursor: 'pointer'}}
                >
                  Mobile Number
                </th>{' '}
                <th className=''>Country</th>{' '}
                <th
                  className=''
                  // onClick={() => handleSort('CityVillage')}
                  // style={{cursor: 'pointer'}}
                >
                  City/Village
                </th>{' '}
                <th
                  className=''
                  style={{cursor: 'pointer'}}
                  onClick={() => handleSort('jaapLiveStartTime')}
                >
                  {' '}
                  Jaap Live Start Time {renderSortIcon('jaapLiveStartTime')}{' '}
                </th>{' '}
                <th
                  className=''
                  style={{cursor: 'pointer'}}
                  onClick={() => handleSort('isLiveForJaap')}
                >
                  {' '}
                  Jaap Status {renderSortIcon('isLiveForJaap')}{' '}
                </th>{' '}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <LoaderInTable loading column={4} />
              ) : (
                <>
                  {data.map((item) => (
                    <tr key={item.memberID}>
                      {/* Member */}
                      <td>
                        <div
                          className='symbol symbol-45px me-5 cursor-pointer'
                          onClick={() => handleShow_Image(item.photoPath)}
                        >
                          <img src={`${pathUrl}${item.photoPath}`} alt={'img'} />
                        </div>
                      </td>

                      {/* Mobile */}
                      <td>
                        <span className='mlr-mobile'>{item.mobileNumber}</span>
                      </td>
                      <td>
                        <span className='text-dark text-hover-primary fs-6 ps-2'>
                          {item.countryCode} {item.countryName}
                        </span>
                      </td>
                      <td>
                        <span className='text-dark text-hover-primary fs-6 ps-2'>
                          {item.villageCityName}
                        </span>
                      </td>

                      {/* Time */}
                      <td>
                        <span className='mlr-time fs-6'>{item.jaapLiveStartTime || '—'}</span>
                      </td>

                      {/* Status */}
                      <td>
                        {item.isLiveForJaap ? (
                          <span className='mlr-status mlr-status-live'>
                            <span className='mlr-status-live-dot' />
                            Live
                          </span>
                        ) : (
                          <span className='mlr-status mlr-status-offline'>
                            <span className='mlr-status-offline-dot' />
                            Offline
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  <BlankDataImageInTable length={data.length} loading={loading} colSpan={4} />
                </>
              )}
            </tbody>
          </table>
        </div>
        <div className='text-center'>
          <Pagination
            total={total}
            current={page}
            pageSize={postPerPage}
            onChange={(p) => {
              setPage(p)
            }}
            showSizeChanger
            onShowSizeChange={(current, size) => {
              setPostPerPage(size)
              setPage(1)
            }}
            showTotal={(total) => `Total ${total} items`}
          />
        </div>
      </div>
      {/* =====================Image Model=================== */}
      <ModelPopUp_ShowImage
        imageShow={`${pathUrl}${state.imageShow}`}
        pageName='Member Image'
        show={showImage}
        handleClose={handleClose_image}
      />
    </>
  )
}

export default MemberLiveReportList
